import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Switch, useLocation } from 'react-router-dom';
import { ThemeProvider as BootstrapThemeProvider } from 'react-bootstrap';
import { Amplify } from '@aws-amplify/core';
import { Auth, CognitoUser } from '@aws-amplify/auth';
import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import {
	buildPlatformRedirectUrl,
	getCachedDeviceKey,
	getEncryptedTokens,
	getSafeRedirectPath,
	refreshCognitoUserSession,
	storeRedirectPath,
} from '@/lib/utils';
import config from '@/lib/config';
import { PlatformModel } from '@/lib/models';
import { publicService } from '@/lib/services';
import { usePlatformsContext, useQuery } from '@/hooks';
import {
	AlertProvider,
	AutoSignInProvider,
	ThemeProvider as MasterClientThemeProvider,
	PlatformsProvider,
} from '@/contexts';
import { Alert, Footer, Loader, PrivateRoute } from '@/components';
import { prefixes } from '@/jss/bootstrap';
import { useGlobalStyles } from '@/jss/hooks/use-global-styles';
import { useThemedBootstrapStyles } from '@/jss/hooks/use-themed-bootstrap-styles';
import 'react-phone-number-input/style.css';

import { routes } from '@/routes';

Amplify.configure({
	region: config.HACKETT_USER_AUTH_AMPLIFY_REGION,
	userPoolId: config.HACKETT_USER_AUTH_AMPLIFY_USER_POOL_ID,
	userPoolWebClientId: config.HACKETT_USER_AUTH_AMPLIFY_USER_POOL_WEB_CLIENT_ID,
});

// Wrapped by <Router>, allows use of 'react-router-dom' hooks
const RoutedApp = () => {
	const location = useLocation();
	const { setPlatforms } = usePlatformsContext();

	const query = useQuery();
	const redirectUrl = decodeURIComponent(query.get('redirectUrl') || '');
	const platform = useMemo(() => query.get('platform') ?? '', [query]);

	const [isLoading, setIsLoading] = useState(true);
	const appIsLoaded = useRef(false);

	// Scroll to top of page on pathname change
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location.pathname]);

	const redirectAuthenticatedUser = useCallback(async (signOnUrl?: string) => {
		const currentUser: CognitoUser = await Auth.currentAuthenticatedUser();
		await refreshCognitoUserSession(currentUser);

		if (!signOnUrl) {
			throw new Error('signOnUrl is undefined.');
		}

		const encryptedTokens = await getEncryptedTokens(currentUser);
		const deviceKey = getCachedDeviceKey(currentUser);
		window.location.href = buildPlatformRedirectUrl({ signOnUrl, tokensHolder: encryptedTokens, deviceKey });
	}, []);

	// Only the path of redirectUrl is kept, and only when it belongs to the platform origin,
	// so the user is sent back to their destination after they authenticate.
	const fetchPlatform = useCallback(async (): Promise<PlatformModel | undefined> => {
		if (!platform) {
			storeRedirectPath(null);
			return;
		}

		try {
			const response = await publicService.getPlatforms({ platform: [platform] }).fetch();
			if (response.platforms.length === 0) {
				throw new Error('No Platform');
			}

			setPlatforms(response.platforms);
			storeRedirectPath(getSafeRedirectPath(redirectUrl, response.platforms[0].signOnUrl));
			return response.platforms[0];
		} catch (error) {
			const typedError = error as Error;
			if (typedError.message === 'No Platform') {
				const params = new URLSearchParams(window.location.search);
				params.delete('platform');
				const remainingParams = params.toString();
				window.location.href = `/sign-in${remainingParams ? `?${remainingParams}` : ''}`;
			}
			return undefined;
		}
	}, [platform, redirectUrl, setPlatforms]);

	const initApp = useCallback(async () => {
		if (appIsLoaded.current) {
			return;
		}

		const changeAuthRoutes = ['/change-password', '/change-mfa-method'];
		const isChangeAuthRoute = changeAuthRoutes.some((route) => window.location.pathname.includes(route));

		setIsLoading(true);

		// The platform is resolved first so an already authenticated user is only redirected to its signOnUrl origin
		const fetchedPlatform = await fetchPlatform();

		// Change auth routes load the app without checking cognito user, refresh, or redirect
		if (!isChangeAuthRoute) {
			try {
				await redirectAuthenticatedUser(fetchedPlatform?.signOnUrl);
				return;
			} catch (_authError) {
				// Not authenticated, render the requested page
			}
		}

		appIsLoaded.current = true;
		setIsLoading(false);
	}, [fetchPlatform, redirectAuthenticatedUser]);

	useEffect(() => {
		initApp();
	}, [initApp]);

	return (
		<>
			{isLoading ? (
				<div className="pt-20 d-flex justify-content-center align-items-center">
					<Loader />
				</div>
			) : (
				<>
					<Alert />
					<Switch>
						{routes.map((route, index) => {
							return (
								<Route key={index} path={route.path} exact={route.exact} children={<route.header />} />
							);
						})}
					</Switch>
					<Switch>
						{routes.map((route, index) => {
							return React.createElement(route.private ? PrivateRoute : Route, {
								key: index,
								path: route.path,
								exact: route.exact,
								children: <route.main location={location} />,
							});
						})}
					</Switch>
					<Footer />
				</>
			)}
		</>
	);
};

// Wrapped by various <ThemeProviders>, allows use of 'jss' hooks
const ThemedApp = () => {
	useGlobalStyles();
	useThemedBootstrapStyles();

	return (
		<Router>
			<RoutedApp />
		</Router>
	);
};

// Exported App, not wrapped by anything, can add various context providers here
const App = () => {
	return (
		<MasterClientThemeProvider>
			<BootstrapThemeProvider prefixes={prefixes}>
				<AutoSignInProvider>
					<AlertProvider>
						<PlatformsProvider>
							<ThemedApp />
						</PlatformsProvider>
					</AlertProvider>
				</AutoSignInProvider>
			</BootstrapThemeProvider>
		</MasterClientThemeProvider>
	);
};

Sentry.init({
	dsn: config.SENTRY_DSN,
	release: __VERSION__,
	integrations: [
		new Sentry.BrowserTracing({
			routingInstrumentation: Sentry.reactRouterV5Instrumentation(createBrowserHistory()),
		}),
		// DO NOT USE, causes performance slow down
		//new Sentry.Replay(),
	],

	// Performance Monitoring
	tracesSampleRate: 1.0, // Capture 100% of the transactions
	// Session Replay
	replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

export default App;
